/** A page-level key: `/` focuses the search box, `c` does what Centre on focus does, `f` toggles full screen. */
export type Shortcut = 'search' | 'centre' | 'fullscreen';

const SHORTCUTS: ReadonlyMap<string, Shortcut> = new Map([
  ['/', 'search'],
  ['c', 'centre'],
  ['f', 'fullscreen'],
]);

/** Elements that take typed keys for themselves. */
const TYPING_TAGS: ReadonlySet<string> = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

/**
 * The shortcut a key press asks for, or `null`: for any other key, a key pressed with Ctrl, Cmd, or Alt
 * (`modified`), and a key typed into a field (`targetTag`) or into an element that owns its keys (`ownsKeys`, see
 * `ownsKeys`).
 */
export function shortcutFor(
  key: string,
  targetTag: string,
  { modified = false, ownsKeys = false }: { modified?: boolean; ownsKeys?: boolean } = {},
): Shortcut | null {
  if (modified || ownsKeys || TYPING_TAGS.has(targetTag.toUpperCase())) return null;
  return SHORTCUTS.get(key) ?? null;
}

/**
 * An editable element, or one inside an open Headless UI menu or listbox (which read letters as typeahead), a
 * dialog over the page, or the list mode's tree (whose keys move through its rows).
 */
export const ownsKeys = (target: HTMLElement): boolean =>
  target.isContentEditable ||
  target.closest('[role="menu"], [role="listbox"], [role="dialog"], [role="tree"]') !== null;
